import type { FormEvent } from "react";
import CouponsIcon from "../assets/CouponsIcon.svg";
import { GiftCardType, GiftCardUiModalConfigType } from "../types";
import {
  DEFAULT_MODAL_SUBTEXT,
  DEFAULT_MODAL_TITLE,
  RECHARGE_NOTICE_DURATION_MS,
  RECHARGE_NOTICE_TRANSITION_MS,
  defaultFormatAmount,
} from "../commons/constants";
import { motion, useReducedMotion } from "motion/react";
import { useGiftCardContext } from "../context/GiftProvider";
import GiftCardProviderChip from "../components/GiftCardProviderChip";
import GiftCardInputRegion from "../components/GiftCardInputRegion";
import PrimaryButton from "../../buttons/PrimaryButton";
import { hexToRgba } from "../../utils/colorUtils";
import GiftCardSuccessStrip from "../components/GiftCardSuccessStrip";

const ZERO_BALANCE_SUBTEXT = "";

export interface GiftCardEntryScreenProps {
  accentColor: string;
  uiModalConfig?: GiftCardUiModalConfigType;
  /** Formats the amount a top-up credited. Pass the app's currency formatter. */
  formatAmount?: (value: number) => string;
}
const GiftCardEntryScreen = ({
  accentColor,
  uiModalConfig,
  formatAmount = defaultFormatAmount,
}: GiftCardEntryScreenProps) => {
  const {
    giftCards,
    selectProvider,
    activeProvider,
    setInputValue,
    setRedeemAmount,
    setActiveStep,
    canRechargeWallet,
    actionState,
    validateActiveCard,
    applyActiveCard,
    rechargeNotice,
    focusRedeemAmount,
    playIntro,
    dismissRechargeNotice,
    dashboardOnly,
  } = useGiftCardContext();

  const reduceMotion = useReducedMotion();
  const noticeTransitionMs = reduceMotion ? 0 : RECHARGE_NOTICE_TRANSITION_MS;
  const noticeKey =
    rechargeNotice && `${rechargeNotice.provider}-${rechargeNotice.amount}`;

  const cards = Object.values(giftCards).filter(
    (card): card is GiftCardType => !!card,
  );
  const isWalletEmpty =
    activeProvider?.provider === "GYFTR" && activeProvider.redeemBalance === 0;

  // A failed balance lookup leaves `redeemBalance` at its zero default too, so `isWalletEmpty`
  // can't tell "genuinely empty" from "unknown" apart. The strip below covers both — this is
  // only what decides which of the two it says.
  const balanceLookupError = isWalletEmpty ? actionState.VALIDATING.error : null;

  // The lookup's own failure is shown by the strip below, not repeated inline — anything else
  // (an apply failure) still belongs under the amount.
  const entryError = balanceLookupError ? actionState.APPLYING.error : actionState.VALIDATING.error ?? actionState.APPLYING.error;

  // The notice names the card it credited, not whichever one is selected now.
  const rechargedLabel =
    rechargeNotice && giftCards[rechargeNotice.provider]?.display_config?.label;
  const rechargeMessage =
    rechargeNotice &&
    `${formatAmount(rechargeNotice.amount)} added to your ${
      rechargedLabel ? `${rechargedLabel} card` : "balance"
    }`;

  const getButtonText = () => {
    if (
      activeProvider?.amount_strategy == "CUSTOM" &&
      !activeProvider.codeValidated
    )
      return "Validate";
    return "Apply";
  };

  // An empty (or unreachable) GYFTR wallet has nothing to spend, so applying is not a move the
  // shopper has — topping the wallet up is. Only that button stays.
  const hideApply = isWalletEmpty;

  // The wallet is looked up as soon as it is selected and reports zero until it answers, so the
  // notice waits for that call to settle — for a genuine zero balance, or for the lookup itself
  // having failed, either of which leaves the shopper with nothing to redeem right now.
  const showZeroBalanceNotice =
    isWalletEmpty &&
    !actionState.VALIDATING.loading &&
    !actionState.RECHARGING.loading;

  const zeroBalanceMessage = balanceLookupError
    ? balanceLookupError.message
    : `Your gift card balance is ${formatAmount(0)}!`;

  const ActionClick = async () => {
    if (
      activeProvider?.amount_strategy == "CUSTOM" &&
      !activeProvider.codeValidated
    ) {
      await validateActiveCard();
      return;
    }
    await applyActiveCard();
  };

  const isActionLoading =
    actionState.APPLYING.loading || actionState.VALIDATING.loading;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Nothing to submit into when the apply button itself is hidden (empty GYFTR wallet) or
    // already mid-request.
    if (hideApply || isActionLoading) return;
    ActionClick();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="ui-flex ui-w-full ui-min-h-0 ui-flex-col"
    >
      <div className="ui-flex ui-px-5 ui-py-4 ui-items-center ui-gap-3">
        <img src={CouponsIcon} alt="" />
        <div>
          <p className="ui-text-[#27272A] ui-text-text-lg-semibold">
            {uiModalConfig?.title || DEFAULT_MODAL_TITLE}
          </p>
          <p className="ui-text-[#27272A] ui-text-text-sm-medium ui-opacity-60">
            {uiModalConfig?.subtext || DEFAULT_MODAL_SUBTEXT}
          </p>
        </div>
      </div>

      {cards.length > 1 && (
        <div
          className="ui-px-5 ui-py-3 ui-grid ui-gap-2"
          style={{
            gridTemplateColumns: `repeat(${Math.min(cards.length, 3)}, 1fr)`,
          }}
        >
          {cards.map((card) => (
            <button
              key={card.provider}
              type="button"
              onClick={() => selectProvider(card.provider)}
            >
              <GiftCardProviderChip
                iconUrl={card.display_config.iconUrl ?? ""}
                label={card.display_config.label}
                accentColor={accentColor}
                showCheckBadge={card.is_active}
                showAccentRing={card.is_active}
              />
            </button>
          ))}
        </div>
      )}

      {activeProvider && (
        <GiftCardInputRegion
          card={activeProvider}
          accentColor={accentColor}
          onInputChange={setInputValue}
          onRedeemAmountChange={setRedeemAmount}
          error={entryError}
          playIntro={playIntro}
          focusRedeemAmount={focusRedeemAmount}
          showZeroBalanceNotice={!rechargeMessage && showZeroBalanceNotice}
          zeroBalanceMessage={zeroBalanceMessage}
          zeroBalanceSubtext={ ZERO_BALANCE_SUBTEXT}
          dashboardOnly={dashboardOnly}
        />
      )}

      <div
        className="ui-mt-auto ui-flex ui-flex-col ui-pb-6"
        style={{ paddingTop: !rechargeMessage && showZeroBalanceNotice ? 0 : undefined }}
      >
        {rechargeMessage && (
          <motion.div
            key={noticeKey}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            transition={{
              duration: noticeTransitionMs / 1000,
              ease: "easeOut",
              repeat: 1,
              repeatType: "reverse",
              repeatDelay:
                (RECHARGE_NOTICE_DURATION_MS - noticeTransitionMs) / 1000,
            }}
            onAnimationComplete={dismissRechargeNotice}
            className="ui-overflow-hidden"
          >
            <GiftCardSuccessStrip message={rechargeMessage} />
          </motion.div>
        )}

        <div className="ui-flex ui-gap-2 ui-bg-white ui-px-4 ui-shadow-[0_-4px_12px_4px_#FFF]">
          {activeProvider?.provider == "GYFTR" && canRechargeWallet && (
            <PrimaryButton
              type="button"
              appearance="outline"
              height="ui-h-[52px]"
              buttonText="Add Balance"
              customization={{
                borderColor: hexToRgba(accentColor, 0.2),
                textColor: accentColor,
                hoverBackgroundColor: "#ffffff",
              }}
              onClick={() => setActiveStep("WALLET_TOPUP")}
              isLoading={actionState.VALIDATING.loading}
            />
          )}

          {!hideApply && (
            <PrimaryButton
              type="submit"
              height="ui-h-[52px]"
              buttonText={getButtonText()}
              customization={{ backgroundColor: accentColor, hoverBackgroundColor: accentColor }}
              isLoading={isActionLoading}
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default GiftCardEntryScreen;
